import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Link,
  Divider,
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const PublicFooter = () => {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'grey.900',
        color: 'white',
        py: 6,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                color: '#ff9800',
                fontWeight: 600,
                mb: 2,
              }}
            >
              Vendoroll
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 3,
                color: 'grey.300',
                lineHeight: 1.6,
              }}
            >
              Connecting local businesses with event opportunities across Canada.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton
                component="a"
                href="https://instagram.com/vendoroll"
                target="_blank"
                sx={{ 
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <InstagramIcon />
              </IconButton>
              <IconButton
                component="a"
                href="https://vendoroll.com"
                target="_blank"
                sx={{ 
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <LanguageIcon />
              </IconButton>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                color: '#ff9800',
                fontWeight: 600,
                mb: 2,
              }}
            >
              Quick Links
            </Typography>
            <List sx={{ p: 0 }}>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  component={RouterLink}
                  to="/for-vendors"
                  sx={{ 
                    color: 'grey.300',
                    '&:hover': {
                      color: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  For Vendors
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  component={RouterLink}
                  to="/for-organizers"
                  sx={{ 
                    color: 'grey.300',
                    '&:hover': {
                      color: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  For Organizers
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => document.querySelector('form').scrollIntoView({ behavior: 'smooth' })}
                  sx={{ 
                    color: 'grey.300',
                    '&:hover': {
                      color: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Join Waitlist
                </ListItemButton>
              </ListItem>
            </List>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                color: '#ff9800',
                fontWeight: 600,
                mb: 2,
              }}
            >
              Contact Us
            </Typography>
            <List sx={{ p: 0 }}>
              <ListItem sx={{ px: 0, mb: 2 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <EmailIcon sx={{ color: '#ff9800' }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Link
                      href="mailto:info@vendoroll.com"
                      sx={{ 
                        color: 'grey.300',
                        textDecoration: 'none',
                        '&:hover': {
                          color: 'white',
                        },
                      }}
                    >
                      info@vendoroll.com
                    </Link>
                  }
                />
              </ListItem>
              <ListItem sx={{ px: 0, mb: 2 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PhoneIcon sx={{ color: '#ff9800' }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Link
                      href="tel:+15875748457"
                      sx={{ 
                        color: 'grey.300',
                        textDecoration: 'none',
                        '&:hover': {
                          color: 'white',
                        },
                      }}
                    >
                      (587) 574-8457
                    </Link>
                  }
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <LocationIcon sx={{ color: '#ff9800' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Calgary, Alberta, Canada"
                  sx={{ color: 'grey.300' }}
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Divider sx={{ my: 4, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            flexWrap: 'wrap', 
            gap: 2,
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'grey.400',
              fontSize: '0.875rem',
            }}
          >
            © {new Date().getFullYear()} Vendoroll. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link
              href="/privacy"
              sx={{ 
                color: 'grey.400',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: 'white',
                },
              }}
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms"
              sx={{ 
                color: 'grey.400',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: 'white',
                },
              }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicFooter; 